import { useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { CheckCircle, FileText, LocateFixed, PenLine, RotateCcw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function SignaturePad({ value, onChange }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const hasInkRef = useRef(Boolean(value));

  const point = event => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (event.clientX - rect.left) * (canvas.width / rect.width),
      y: (event.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const start = event => {
    event.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const p = point(event);
    drawingRef.current = true;
    hasInkRef.current = true;
    canvas.setPointerCapture?.(event.pointerId);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };

  const move = event => {
    if (!drawingRef.current) return;
    event.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = point(event);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };

  const finish = event => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    canvasRef.current.releasePointerCapture?.(event.pointerId);
    if (hasInkRef.current) onChange(canvasRef.current.toDataURL('image/png'));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    hasInkRef.current = false;
    onChange('');
  };

  useEffect(() => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.strokeStyle = '#163f3a';
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  useEffect(() => {
    if (!value) return;
    const canvas = canvasRef.current;
    const image = new Image();
    image.onload = () => {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#163f3a';
      ctx.lineWidth = 4;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      hasInkRef.current = true;
    };
    image.src = value;
  }, [value]);

  return (
    <div>
      <div className="relative rounded-xl border-2 border-dashed border-isk-teal/40 bg-white overflow-hidden">
        <canvas
          ref={canvasRef}
          width={800}
          height={260}
          className="block w-full h-40 touch-none cursor-crosshair"
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={finish}
          onPointerCancel={finish}
          aria-label="Area tanda tangan"
        />
        {!value && (
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center text-isk-muted/60 text-sm">
            Tanda tangan di area ini
          </div>
        )}
      </div>
      <button type="button" onClick={clear} className="mt-2 inline-flex items-center gap-1 text-xs text-isk-teal hover:underline">
        <RotateCcw className="w-3.5 h-3.5" /> Hapus dan ulangi
      </button>
    </div>
  );
}

export default function AgreementStep({
  readToEnd,
  setReadToEnd,
  accepted,
  onAccepted,
  signature,
  setSignature,
  coordinates,
  locationError,
  manualLocation,
  setManualLocation,
  locating,
  requestLocation,
}) {
  const [open, setOpen] = useState(false);
  const [numPages, setNumPages] = useState(0);
  const [pdfError, setPdfError] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && !readToEnd && scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [open, readToEnd]);

  const checkScroll = event => {
    const el = event.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= 24) setReadToEnd(true);
  };

  const acceptAgreement = event => {
    const checked = event.target.checked;
    onAccepted(checked);
    if (checked) setOpen(false);
  };

  const hasLocation = Boolean(manualLocation.trim());

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold text-isk-text">Peraturan dan Tanda Tangan</h3>
        <p className="text-xs text-isk-muted mt-1">Baca dokumen sampai selesai sebelum memberikan persetujuan.</p>
      </div>

      <div className={`p-4 rounded-2xl border-2 ${accepted ? 'border-green-300 bg-green-50' : 'border-isk-teal/20 bg-isk-teal/5'}`}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shrink-0">
            <FileText className="w-5 h-5 text-isk-teal" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-isk-text">Perjanjian Sewa Unit Kost ISK House</p>
            <p className="text-xs text-isk-muted mt-0.5">2 halaman · PDF</p>
            <Button type="button" onClick={() => setOpen(true)} className="mt-3 rounded-full bg-isk-teal text-white h-8 text-xs">
              {readToEnd ? 'Baca Ulang Dokumen' : 'Buka dan Baca Dokumen'}
            </Button>
          </div>
          {accepted && <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />}
        </div>
      </div>

      {!accepted && readToEnd && (
        <p className="text-xs text-isk-muted">Buka dokumen kembali lalu centang persetujuan yang muncul di bagian bawah popup.</p>
      )}

      {accepted && (
        <div className="space-y-4">
          <div>
            <Label className="flex items-center gap-1.5"><PenLine className="w-4 h-4" /> Tanda Tangan *</Label>
            <p className="text-xs text-isk-muted mt-1 mb-2">Gunakan jari, mouse, atau touchpad.</p>
            <SignaturePad value={signature} onChange={setSignature} />
          </div>

          <div className="p-3 rounded-xl border border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label className="flex items-center gap-1.5"><LocateFixed className="w-4 h-4" /> Lokasi Penandatanganan *</Label>
                {coordinates && locating && <p className="text-xs text-isk-muted mt-1">Mencari daerah terdekat dari titik GPS...</p>}
                {coordinates && !locating && manualLocation && <p className="text-xs text-green-700 mt-1">Daerah terdekat berhasil ditemukan</p>}
                {!coordinates && !locationError && <p className="text-xs text-isk-muted mt-1">Lokasi akan disimpan bersama tanggal penandatanganan.</p>}
              </div>
              {!hasLocation && (
                <Button type="button" variant="outline" onClick={requestLocation} disabled={locating} className="rounded-full h-8 text-xs shrink-0">
                  {locating ? 'Mencari...' : 'Ambil Lokasi'}
                </Button>
              )}
            </div>
            {locationError && !hasLocation && !locating && (
              <div className="mt-3">
                <p className="text-xs text-orange-700 mb-2">{locationError} Isi lokasi secara manual untuk melanjutkan.</p>
                <Input
                  value={manualLocation}
                  onChange={event => setManualLocation(event.target.value)}
                  placeholder="Contoh: Kemayoran, Jakarta Pusat"
                  className="rounded-xl bg-white"
                  maxLength={250}
                />
              </div>
            )}
            {hasLocation && manualLocation && <p className="text-xs text-green-700 mt-2">Lokasi: {manualLocation}</p>}
            {hasLocation && coordinates && (
              <p className="text-[11px] text-isk-muted mt-1">Nama daerah berdasarkan © OpenStreetMap contributors</p>
            )}
          </div>

          <div className="p-3 rounded-xl bg-isk-gold/10 border border-isk-gold/20 text-xs text-isk-muted">
            Tanggal dan waktu penandatanganan akan dicatat otomatis oleh sistem saat pendaftaran dikirim.
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-[200] bg-black/70 flex items-center justify-center p-3 sm:p-6">
          <div className="bg-white rounded-2xl w-full max-w-4xl h-[92vh] flex flex-col overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b">
              <div>
                <p className="font-semibold text-isk-text text-sm">Perjanjian Sewa Kost</p>
                <p className="text-xs text-isk-muted">Gulir sampai halaman terakhir</p>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="p-2 rounded-full hover:bg-gray-100" aria-label="Tutup dokumen">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div ref={scrollRef} onScroll={checkScroll} className="flex-1 overflow-y-auto bg-gray-200 px-2 sm:px-5 py-4">
              <Document
                file={`${BACKEND_URL}/api/tenant/agreement-template`}
                onLoadSuccess={({ numPages: total }) => { setNumPages(total); setPdfError(''); }}
                onLoadError={() => setPdfError('Dokumen gagal dimuat. Silakan tutup dan coba lagi.')}
                loading={<div className="py-20 text-center text-sm text-isk-muted">Memuat dokumen...</div>}
              >
                {Array.from({ length: numPages }, (_, index) => (
                  <div key={index} className="mb-4 flex justify-center">
                    <Page pageNumber={index + 1} width={Math.min(720, window.innerWidth - 48)} />
                  </div>
                ))}
              </Document>
              {pdfError && <div className="p-4 rounded-xl bg-red-50 text-red-700 text-sm text-center">{pdfError}</div>}
              {readToEnd && !pdfError && <div className="h-4" />}
            </div>

            <div className="px-4 py-3 border-t bg-white">
              {readToEnd ? (
                <label className="flex items-start gap-2.5 cursor-pointer text-sm text-isk-text">
                  <input
                    type="checkbox"
                    checked={accepted}
                    onChange={acceptAgreement}
                    className="mt-0.5 w-4 h-4 accent-isk-teal shrink-0"
                  />
                  <span>Saya telah membaca, memahami, dan menyetujui seluruh peraturan serta ketentuan dalam dokumen ini.</span>
                </label>
              ) : (
                <p className="text-xs text-center text-isk-muted">Checklist persetujuan akan muncul setelah dokumen digulir sampai bawah.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
